import { useEffect, useRef, useState } from "react";
import { UploadFileInfo } from "@progress/kendo-react-upload";
import { useTranslation } from "react-i18next";
import styles from "styles.module.css";

import { useUserOrganizationsStore } from "store/user";

import { usePageAccess } from "hooks/usePageAccess";
import { IVesselDocument, IVesselFolder } from "interface/vessel";
import { useParams } from "react-router";
import {
	deleteVesselDocumentApi,
	deleteVesselFolderApi,
	getVesselDocumentsApi,
	getVesselFoldersApi,
	updateVesselDocumentDescriptionApi,
	uploadVesselDocumentApi,
	VesselDocumentFilterOptions,
} from "api/vessel";
import Filter from "./Filter";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { DocumentsGridColumnItem, FoldersGridColumnItem } from "./GridColumns";
import { openFile } from "./DocumentFileUpload";
import DocumentUploadModal, {
	DocumentUploadModalHandle,
} from "./DocumentUploadModal";
import Loading from "ui/Loading";
import { COLOR, ICON_NAMES } from "utils/constant";
import { useInfoStore } from "store/error";
import { infoHandler } from "./utilities/helper";
import GridCustomPage from "ui/grid/GridPage";
import CustomDetailedPage from "ui/grid/DetailPage";
import { GridItemChangeEvent } from "@progress/kendo-react-grid";

export default function VesselDocuments() {
	const { t } = useTranslation();

	const organization = useUserOrganizationsStore((state) => state.defaultOrg);
	const [filterOptions, setFilterOptions] =
		useState<VesselDocumentFilterOptions>({
			vesselId: "",
			vesselRegistrationId: "",
		});
	const foldersRef = useRef<IVesselFolder[]>([]);
	const [expanded, setExpanded] = useState<
		{ [key: string]: boolean } | undefined
	>(undefined);

	const openFolderRef = useRef<IVesselFolder | null>(null);
	const [ongoingAction, setOngoingAction] = useState({
		deleting_file: false,
		uploading_file: false,
		deleting_folder: false,
	});

	const [folderGridRefresh, setFolderGridRefresh] = useState(false);

	const deletingVessel = useRef<IVesselDocument | null>(null);

	const excelRef = useRef<ExcelExport>(null);

	const [isUploading, setIsUploading] = useState(false);
	const uploadingBatch = useRef(false);

	const { id, action: type } = useParams<{ id: string; action: string }>();
	const uploadModalRef = useRef<DocumentUploadModalHandle>(null);
	const { pageAccess, loading: pageAccessLoading } = usePageAccess();
	const setInfo = useInfoStore((state) => state.setInfo);
	const resetStore = useInfoStore((state) => state.resetStore);
	const viewDocumentRef = useRef<IVesselDocument | null>(null);

	useEffect(() => {
		const filter = {
			vesselId: "", // organization.vesselId,
			vesselRegistrationId: "", // organization.vesselRegistrationNumber,
			vesselName: "",
		};

		if (!id || !type) {
			if (organization) {
				filter.vesselId = organization.vesselId;
				filter.vesselRegistrationId = organization.vesselRegistrationNumber;
				filter.vesselName = organization.displayName;
			}
		} else {
			filter.vesselId = id;
			filter.vesselRegistrationId = type;
			filter.vesselName = type;
		}

		setFilterOptions(filter);
		foldersRef.current = [];
		openFolderRef.current = null;
		setExpanded(undefined);
	}, [organization, id, type]);

	const updateVesselDocumentDescription = async (data: {
		documentId: string;
		vesselId: string;
		vesselRegistrationId: string;
		vesselFolderId?: string;
		description: string;
	}) => {
		try {
			const response = await updateVesselDocumentDescriptionApi(data);
			return response;
		} catch (error: any) {
			let info = infoHandler("Error", t("Error"), t("ErrorDescription"));
			if (error?.status === 403) {
				info.title = t("AccessDenied");
				info.description = t("UploadAccessDeniedMessage");
			}
			setInfo(info);
			console.error(error);
		}
	};
	const getDocuments = async () => {
		if (
			!filterOptions.vesselId ||
			!filterOptions.vesselRegistrationId ||
			!pageAccess ||
			!openFolderRef.current?.id ||
			pageAccess?.read === false
		) {
			return [];
		}
		try {
			const response = await getVesselDocumentsApi({
				...filterOptions,
				vesselFolderId: openFolderRef.current?.id,
			});
			return response;
		} catch (error: any) {
			let info = infoHandler(
				"Error",
				t("UploadError"),
				t("UploadErrorMessage"),
			);
			if (error?.status === 403) {
				info.title = t("AccessDenied");
				info.description = t("UploadAccessDeniedMessage");
			}
			setInfo(info);
			console.error(error);
		}
	};

	const getVesselFolders = async () => {
		if (foldersRef.current.length > 0) {
			return { data: foldersRef.current };
		}

		if (!filterOptions.vesselId || !filterOptions.vesselRegistrationId) {
			return { data: [] };
		}

		try {
			const response = await getVesselFoldersApi({
				vesselId: filterOptions.vesselId,
				vesselRegistrationId: filterOptions.vesselRegistrationId,
			});
			if (response && response.data.length === 1) {
				foldersRef.current = response.data;
				if (!expanded) {
					setExpanded({ [response.data[0].id]: true });
				}
			}

			return response;
		} catch (error: any) {
			if (error?.status === 403) {
				setInfo(
					infoHandler(
						"Error",
						t("AccessDenied"),
						t("VesselFoldersLoadAccessDenied"),
					),
				);
			} else {
				setInfo(infoHandler("Error", t("Error"), t("VesselFoldersLoadError")));
			}
			console.error(error);
		}
	};

	const handleUpload = async (
		files: UploadFileInfo[],
	): Promise<UploadFileInfo[]> => {
		const folderId = openFolderRef.current?.id;
		if (
			!pageAccess?.create ||
			!folderId ||
			uploadingBatch.current ||
			files.length === 0
		)
			return files;
		const scope = { ...filterOptions, vesselFolderId: folderId };
		const selectedFiles = [...files];
		const failedFiles: UploadFileInfo[] = [];
		const failures: string[] = [];
		let uploaded = 0;
		uploadingBatch.current = true;
		setIsUploading(true);
		try {
			for (const selected of selectedFiles) {
				const file = selected.getRawFile?.();
				if (!file || file.size === 0) {
					failedFiles.push(selected);
					failures.push(`${selected.name}: ${t("AttachmentEmptyFile")}`);
					continue;
				}
				try {
					await uploadVesselDocumentApi(file, scope);
					uploaded++;
				} catch (error: any) {
					failedFiles.push(selected);
					const status = error?.response?.status ?? error?.status;
					const reason =
						status === 409
							? t("FileNameExistsError")
							: status === 403
								? t("UploadAccessDeniedMessage")
								: t("UploadErrorMessage");
					failures.push(`${file.name}: ${reason}`);
				}
			}
			if (uploaded > 0) {
				// setDocumentRefresh((current) => ({
				// 	...current,
				// 	[folderId]: (current[folderId] ?? 0) + 1,
				// }));
			}
			const summary = t("AttachmentBatchUploadSummary", {
				uploaded,
				failed: failedFiles.length,
			});
			setInfo(
				infoHandler(
					failedFiles.length ? (uploaded ? "Warning" : "Error") : "Success",
					t(failedFiles.length ? "UploadError" : "UploadSuccess"),
					failures.length ? `${summary}\n\n${failures.join("\n")}` : summary,
				),
			);
			return failedFiles;
		} finally {
			uploadingBatch.current = false;
			setIsUploading(false);
		}
	};

	const deleteDocument = async (document: IVesselDocument) => {
		try {
			await deleteVesselDocumentApi({
				...filterOptions,
				documentId: document.id,
				// fileName: document.fileName,
			});
			setInfo({
				title: t("Success"),
				show: true,
				description: t("DocumentDeleteSuccess", {
					name: document.fileName,
				}),
				infoType: "Success",
				actions: [
					{
						text: t("OK"),
						btn: () => resetStore(),
						disabled: false,
						bgColor: COLOR.StatusGreen,
						textColor: "white",
					},
				],
			});
		} catch (error) {
			setInfo(infoHandler("Error", t("Error"), t("AttachmentDeleteError")));
			console.error(error);
		}
	};
	const handleGetVesselDocumentFile = async (
		doc: IVesselDocument,
		view: boolean,
	) => {
		const fileWindow = view ? window.open("about:blank", "_blank") : null;
		if (view && !fileWindow) {
			setInfo({
				title: t("Error"),
				show: true,
				description: t("AttachmentPopupBlocked"),
				infoType: "Error",
				actions: [],
			});
			return;
		}
		if (fileWindow) fileWindow.opener = null;
		try {
			const response = await getVesselDocumentsApi({
				...filterOptions,
				documentId: doc.id,
				includeContent: 1,
			});
			const data = response.data;
			if (!data || data.length === 0 || !data[0].fileByte) {
				setInfo({
					title: t("DocumentNotFound"),
					show: true,
					description: t("RequestedDocumentCouldNotBeFound"),
					infoType: "Error",
					actions: [
						{
							text: t("OK"),
							btn: () => resetStore(),
							disabled: false,
							bgColor: "",
							textColor: "",
						},
					],
				});
				fileWindow?.close();
				return null;
			}
			viewDocumentRef.current = data[0];
			const opened = openFile(viewDocumentRef.current, view, (data) => {
				if (fileWindow && !fileWindow.closed) {
					fileWindow.location.replace(data.url);
					const cleanup = window.setInterval(() => {
						if (fileWindow.closed) {
							URL.revokeObjectURL(data.url);
							window.clearInterval(cleanup);
						}
					}, 1000);
				} else {
					URL.revokeObjectURL(data.url);
				}
			});
			if (!opened) fileWindow?.close();
		} catch (error: any) {
			fileWindow?.close();

			if (error?.status === 403) {
				setInfo(
					infoHandler(
						"Error",
						t("AccessDenied"),
						t("UploadAccessDeniedMessage"),
					),
				);

				return;
			}
			setInfo(infoHandler("Error", t("Error"), t("DownloadError")));
			console.error(error);
		}
	};
	const handleDeleteDocument = (document: IVesselDocument) => {
		deletingVessel.current = document;
		setInfo({
			title: t("DeleteDocument"),
			show: true,
			description: t("ConfirmDocumentDelete", { name: document.fileName }),
			infoType: "Warning",
			actions: [
				{
					text: t("Cancel"),
					btn: () => resetStore(),
					disabled: false,
					bgColor: "",
					textColor: "",
				},
				{
					text: t("Delete"),
					loading: ongoingAction.deleting_file,
					btn: async () => {
						const confirmation = useInfoStore.getState().info;
						if (
							!confirmation ||
							confirmation.actions?.some((action) => action.loading)
						)
							return;
						setInfo({
							...confirmation,
							actions: confirmation.actions?.map((action, index) => ({
								...action,
								disabled: true,
								loading: index === 1,
							})),
						});
						setOngoingAction((current) => ({
							...current,
							deleting_file: true,
						}));
						try {
							await deleteDocument(document);
						} finally {
							deletingVessel.current = null;
							setOngoingAction((current) => ({
								...current,
								deleting_file: false,
							}));
						}
					},
					disabled: false,
					bgColor: "",
					textColor: "",
				},
			],
		});
	};

	const addFileToFolder = (folder: IVesselFolder) => {
		if (!pageAccess?.create || isUploading) return;
		if (!openFolderRef.current || openFolderRef.current.id !== folder.id) {
			openFolderRef.current = folder;
			setExpanded({ [folder.id]: true });
		}

		uploadModalRef.current?.open();
	};

	const confirmDeleteFolder = (folder: IVesselFolder) => {
		if (!pageAccess?.delete) return;
		openFolderRef.current = folder;
		setExpanded({ [folder.id]: true });
		const scope = {
			id: folder.id,
			vesselId: folder.vesselId,
			vesselRegistrationId: folder.vesselRegistrationId,
		};
		setInfo(
			infoHandler(
				"Warning",
				t("VesselFolderDelete"),
				t("VesselFolderConfirmDelete", { name: folder.folderName }),
				[
					{
						text: t("Cancel"),
						btn: resetStore,
						disabled: false,
						bgColor: "",
						textColor: "",
					},
					{
						text: t("Delete"),
						disabled: ongoingAction.deleting_folder,
						bgColor: "",
						textColor: "",
						loading: ongoingAction.deleting_folder,
						btn: async () => {
							const confirmation = useInfoStore.getState().info;
							if (
								!confirmation ||
								confirmation.actions?.some((action) => action.loading)
							)
								return;
							setInfo({
								...confirmation,
								actions: confirmation.actions?.map((action, index) => ({
									...action,
									disabled: true,
									loading: index === 1,
								})),
							});
							setOngoingAction((current) => ({
								...current,
								deleting_folder: true,
							}));
							try {
								await deleteVesselFolderApi(scope);
								if (filterOptions.vesselFolderId === folder.id) {
									uploadModalRef.current?.close();
									setFilterOptions((current) => ({
										...current,
										vesselFolderId: undefined,
									}));
								}
								setOngoingAction((current) => ({
									...current,
									deleting_folder: false,
								}));
								resetStore();
								setFolderGridRefresh((value) => !value);
								setInfo(
									infoHandler(
										"Success",
										t("Success"),
										t("VesselFolderDeleteSuccess"),
									),
								);
							} catch {
								setOngoingAction((current) => ({
									...current,
									deleting_folder: false,
								}));
								setInfo(
									infoHandler(
										"Error",
										t("Error"),
										t("VesselFolderDeleteError"),
									),
								);
							}
						},
					},
				],
			),
		);
	};

	if (pageAccessLoading || !pageAccess) {
		return <Loading show={true} />;
	}

	return (
		<div
			className={styles.PageContainer}
			style={{ height: "auto", minHeight: "calc(100% - 90px)" }}
		>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{t("VesselDocuments")}
				</legend>
			</div>
			{pageAccess?.read && (
				<div style={{ marginTop: "20px" }}>
					<Filter
						canCreate={pageAccess.create}
						onFolderCreated={() => {
							foldersRef.current = [];
							setFolderGridRefresh((value) => !value);
						}}
						setFilterOptions={setFilterOptions}
						filterOptions={filterOptions}

						// exportAsExcel={exportGridAsExcel}
					/>
				</div>
			)}

			<DocumentUploadModal
				ref={uploadModalRef}
				isUploading={isUploading}
				canCreate={pageAccess.create}
				onUpload={handleUpload}
			/>

			<GridCustomPage
				dataKey="id"
				gridColumns={FoldersGridColumnItem({
					canCreate: pageAccess.create,
					canDelete: pageAccess.delete,
					onAddFile: addFileToFolder,
					onDeleteFolder: confirmDeleteFolder,
				})}
				forceUpdate={folderGridRefresh}
				filter={filterOptions}
				// gridData={folders}
				// unUsedHeight={}
				expanded={expanded}
				// pageAccess={pageAccess}
				// exportGridAsPDFRef={pdfRef}
				exportGridAsExcelRef={excelRef}
				// isExpandable={true}
				// lastColWidth="90px"
				pageAccess={pageAccess}
				showDetailIcon={true}
				apiMethod={getVesselFolders}
				excelProps={{
					fileName: `documents_${new Date().toISOString()}.xlsx`,
				}}
				dataEmptyMessage={t("NoVesselDocuments")}
				onDetailExpandChange={(data) => {
					Object.entries(data.detailExpand).forEach(([key, value]) => {
						if (value === false) {
							openFolderRef.current = null;
						}
					});
				}}
				detailPage={(prop) => {
					openFolderRef.current = prop.dataItem;
					return (
						<CustomDetailedPage
							// key={`${prop.dataItem.id}:${documentRefresh[prop.dataItem.id] ?? 0}`}
							grids={DocumentsGridColumnItem({
								canEdit: pageAccess.update,
							})}
							dataApi={getDocuments}
							lastColWidth="130px"
							onItemChange={(e: GridItemChangeEvent) => {
								updateVesselDocumentDescription({
									documentId: e.dataItem.id,
									vesselId: filterOptions.vesselId as any,
									vesselRegistrationId:
										filterOptions.vesselRegistrationId as any,
									vesselFolderId: e.dataItem.vesselFolderId,
									description: e.dataItem.description,
								});
							}}
							lastColBtns={[
								...(pageAccess.delete
									? [
											{
												onclick: (data: any) => {
													const dataItem = data.dataItem;
													handleDeleteDocument(dataItem);
												},
												iconName: ICON_NAMES.Delete,
												alwaysShow: false,
											},
										]
									: []),

								{
									onclick: (data) => {
										const dataItem = data.dataItem;
										handleGetVesselDocumentFile(dataItem, true);
									},
									iconName: ICON_NAMES.Visibility,
								},
								{
									onclick: (data) => {
										const dataItem = data.dataItem;
										handleGetVesselDocumentFile(dataItem, false);
									},
									iconName: ICON_NAMES.Download,
								},
							]}
						/>
					);
				}}
			/>
			{!pageAccess?.read && (
				<div
					className="no-access"
					style={{ marginTop: "60px", textAlign: "center" }}
				>
					<h1>{t("AccessDenied")}</h1>
					<p>{t("AccessDeniedDescription")}</p>
					<p>{t("AccessDeniedAction")}</p>
				</div>
			)}
		</div>
	);
}
